<template>
  <div class="container-fluid py-4 oa-overview oa-workspace">
    <!-- Hero -->
    <div class="row g-4">
      <div class="col-12">
        <div class="card border-0 oa-hero-card overflow-hidden">
          <div class="card-body p-4 p-lg-5">
            <div class="row g-4 align-items-center">
              <div class="col-12">
                <div class="d-inline-flex align-items-center gap-2 oa-eyebrow mb-3">
                  <span class="oa-eyebrow-dot"></span>
                  <span>Pay Operations · Internal documentation</span>
                </div>
                <h2 class="mb-3">Order Amendments — How It Works</h2>
                <p class="text-muted oa-hero-copy mb-4">
                  This section replaces the old Google Sheet for fixing problematic orders. It gives
                  the Pay Operations team a structured way to find work, understand what is wrong,
                  prepare a correction, preview the effects, execute the change, and see exactly
                  what happened to the order, invoice, and refund — all with a complete audit trail.
                  Start with <strong>Which Page for Which Case</strong> below if you just want to
                  know where to click; read the flows when you want to understand what happens
                  behind each button.
                </p>
                <div class="d-flex flex-wrap gap-2">
                  <span class="badge rounded-pill oa-soft-badge">
                    <i class="fa fa-list me-1"></i>6 Menu Pages
                  </span>
                  <span class="badge rounded-pill oa-soft-badge">
                    <i class="fa fa-sitemap me-1"></i>7 Process Flows
                  </span>
                  <span class="badge rounded-pill oa-soft-badge">
                    <i class="fa fa-shield me-1"></i>Full Audit + Preview Safety
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-12">
        <div class="ribbon-wrapper card oa-index-card">
          <div class="card-body">
            <div class="ribbon ribbon-clip ribbon-primary">On this page</div>
            <p class="text-muted mb-3">
              Open a topic to review the working pages, process flows, operational rules, and
              troubleshooting guidance for order amendments.
            </p>
            <div class="row g-2">
              <div v-for="entry in tableOfContents" :key="entry.id" class="col-sm-6 col-xl-4">
                <a
                  :href="`#${entry.id}`"
                  class="oa-index-link h-100"
                  :class="{ 'oa-index-link--open': isSectionOpen(entry.id) }"
                  @click.prevent="openAndScrollToSection(entry.id)"
                >
                  <span class="oa-index-icon"><i class="fa" :class="entry.icon"></i></span>
                  <span class="flex-grow-1">
                    <strong>{{ entry.title }}</strong>
                    <small>{{ entry.hint }}</small>
                  </span>
                  <i class="fa fa-chevron-right oa-index-chevron"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Section 0 — Which page for which case -->
      <div
        :id="sections.useCases"
        class="col-12 scroll-anchor"
        :class="{ 'oa-section-collapsed': !isSectionOpen(sections.useCases) }"
      >
        <div
          class="oa-section-heading"
          role="button"
          tabindex="0"
          :aria-expanded="isSectionOpen(sections.useCases)"
          @click="toggleSection(sections.useCases)"
          @keydown.enter.prevent="toggleSection(sections.useCases)"
          @keydown.space.prevent="toggleSection(sections.useCases)"
        >
          <h4 class="mb-1">Which Page for Which Case</h4>
          <p class="text-muted mb-0">
            Start here. Find your situation on the left and jump straight to the right tool — every
            destination is a click away.
          </p>
        </div>
        <div class="card border border-light shadow-sm">
          <div class="card-body p-4">
            <div v-for="group in useCaseGroups" :key="group.label" class="oa-usecase-group">
              <div class="oa-usecase-group__head">
                <span class="oa-usecase-group__icon">
                  <i class="fa" :class="group.icon" aria-hidden="true"></i>
                </span>
                <span class="oa-usecase-group__label">{{ group.label }}</span>
              </div>
              <ul class="oa-usecase-list">
                <li v-for="uc in group.items" :key="uc.situation" class="oa-usecase-row">
                  <div class="oa-usecase-row__situation">{{ uc.situation }}</div>
                  <div class="oa-usecase-row__target">
                    <router-link :to="uc.to" class="oa-usecase-link">
                      <span class="oa-usecase-link__icon">
                        <i class="fa" :class="uc.icon" aria-hidden="true"></i>
                      </span>
                      <span class="oa-usecase-link__label">{{ uc.target }}</span>
                      <i
                        class="fa fa-chevron-right oa-usecase-link__chevron"
                        aria-hidden="true"
                      ></i>
                    </router-link>
                    <div v-if="uc.targetHint" class="oa-usecase-row__target-hint">
                      {{ uc.targetHint }}
                    </div>
                  </div>
                  <div class="oa-usecase-row__guidance">
                    <p class="mb-1">{{ uc.steps }}</p>
                    <p class="oa-usecase-row__hint mb-0">
                      <i class="fa fa-lightbulb-o" aria-hidden="true"></i>
                      <span>{{ uc.hint }}</span>
                    </p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <!-- Section 1 — Working pages -->
      <div
        :id="sections.threePages"
        class="col-12 scroll-anchor"
        :class="{ 'oa-section-collapsed': !isSectionOpen(sections.threePages) }"
      >
        <div
          class="oa-section-heading"
          role="button"
          tabindex="0"
          :aria-expanded="isSectionOpen(sections.threePages)"
          @click="toggleSection(sections.threePages)"
          @keydown.enter.prevent="toggleSection(sections.threePages)"
          @keydown.space.prevent="toggleSection(sections.threePages)"
        >
          <h4 class="mb-1">The Working Pages</h4>
          <p class="text-muted mb-0">
            Everything in this section lives behind these menu links. Each has one job. The case
            detail, drive-away flow, and bulk-job pages open from the Queue rather than the menu.
          </p>
        </div>
        <div class="row g-3">
          <div v-for="page in pages" :key="page.routeName" class="col-12 col-md-6 col-xl-4">
            <router-link :to="{ name: page.routeName }" class="text-decoration-none d-block h-100">
              <div class="card oa-page-card h-100">
                <div class="card-body p-4">
                  <div class="d-flex align-items-start gap-3">
                    <div class="oa-icon-wrap" :class="page.iconClass">
                      <i class="fa fs-5" :class="page.icon"></i>
                    </div>
                    <div class="flex-grow-1 min-w-0">
                      <div
                        class="d-flex flex-wrap align-items-start justify-content-between gap-2 mb-2"
                      >
                        <span class="fw-semibold text-dark">{{ page.title }}</span>
                        <span v-if="page.roleNote" class="badge oa-role-note">{{
                          page.roleNote
                        }}</span>
                      </div>
                      <p class="text-muted small mb-3">{{ page.summary }}</p>
                      <ul class="oa-bullets small text-muted mb-0">
                        <li v-for="b in page.bullets" :key="b">{{ b }}</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </router-link>
          </div>
        </div>
      </div>

      <!-- Section 2 — Glossary -->
      <div
        :id="sections.glossary"
        class="col-12 scroll-anchor"
        :class="{ 'oa-section-collapsed': !isSectionOpen(sections.glossary) }"
      >
        <div
          class="oa-section-heading"
          role="button"
          tabindex="0"
          :aria-expanded="isSectionOpen(sections.glossary)"
          @click="toggleSection(sections.glossary)"
          @keydown.enter.prevent="toggleSection(sections.glossary)"
          @keydown.space.prevent="toggleSection(sections.glossary)"
        >
          <h4 class="mb-1">Vocabulary</h4>
          <p class="text-muted mb-0">
            These words appear all over the portal and in conversations with engineers. Read once,
            keep handy.
          </p>
        </div>
        <div class="card border border-light shadow-sm">
          <div class="card-body p-0">
            <div class="table-responsive">
              <table class="table table-hover oa-glossary-table mb-0">
                <thead>
                  <tr>
                    <th style="width: 18%">Term</th>
                    <th>What it actually means for you</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="g in glossary" :key="g.term">
                    <td class="fw-semibold align-top">
                      {{ g.term }}
                      <div v-if="g.aka" class="small text-muted fw-normal">aka {{ g.aka }}</div>
                    </td>
                    <td>{{ g.meaning }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <!-- Section 3 — How orders end up in the queue (Flow 1) -->
      <div
        :id="sections.queueFlow"
        class="col-12 scroll-anchor"
        :class="{ 'oa-section-collapsed': !isSectionOpen(sections.queueFlow) }"
      >
        <div
          class="oa-section-heading"
          role="button"
          tabindex="0"
          :aria-expanded="isSectionOpen(sections.queueFlow)"
          @click="toggleSection(sections.queueFlow)"
          @keydown.enter.prevent="toggleSection(sections.queueFlow)"
          @keydown.space.prevent="toggleSection(sections.queueFlow)"
        >
          <h4 class="mb-1">Flow 1 — How Orders End Up in the Queue</h4>
          <p class="text-muted mb-0">
            The portal does not show every order, only the ones that probably need attention. Here
            is the path an order travels.
          </p>
        </div>
        <div class="card border border-light shadow-sm">
          <div class="card-body p-4">
            <div class="oa-flow">
              <div class="oa-step oa-step--source">
                <div class="oa-step__title">Customer creates an order</div>
                <div class="oa-step__desc">
                  Refuel at the pump, EV charging session, car wash, …
                </div>
              </div>
              <div class="oa-arrow"></div>
              <div class="oa-step">
                <div class="oa-step__title">Order Service stores the order</div>
                <div class="oa-step__desc">
                  This is the ryd system that owns all orders. The portal never replaces it — we
                  read live data from it whenever you open an order.
                </div>
              </div>
              <div class="oa-arrow"></div>
              <div class="oa-step oa-step--branch">
                <div class="oa-step__title">Order ends in a "problem" state</div>
                <div class="oa-step__desc">
                  PROBLEM, FAILED, or REJECTED — payment timed out, pump stopped, partner returned
                  an error, etc. Healthy orders are ignored.
                </div>
              </div>
              <div class="oa-arrow"></div>
              <div class="oa-step oa-step--trigger">
                <div class="oa-step__title">
                  <i class="fa fa-clock-o me-1"></i> Scheduler runs automatically
                  <span class="text-muted small fw-normal ms-2">+ manual "Refresh" button</span>
                </div>
                <div class="oa-step__desc">
                  Every few minutes the system asks Order Service for problem orders since the last
                  check. You can also force a refresh from the Queue page.
                </div>
              </div>
              <div class="oa-arrow"></div>
              <div class="oa-step">
                <div class="oa-step__title">Classification</div>
                <div class="oa-step__desc">
                  Each problem order is tagged with one workflow: Standard, Overshoot, EV Reconcile,
                  or Drive Away. The tag is computed in the backend — the page you see never
                  guesses.
                </div>
              </div>
              <div class="oa-arrow"></div>
              <div class="oa-step oa-step--store">
                <div class="oa-step__title">Case saved in the portal</div>
                <div class="oa-step__desc">
                  A "case" is the row you see in the Queue. It carries the classification, status
                  (open / assigned / resolved …), assignee, days open, partner, license plate,
                  amount.
                </div>
              </div>
              <div class="oa-arrow"></div>
              <div class="oa-step oa-step--end">
                <div class="oa-step__title">It appears in your Queue</div>
                <div class="oa-step__desc">
                  Filter by tab, partner, assignee, classification, status. Click a row to open the
                  case detail page.
                </div>
              </div>
            </div>

            <div class="alert alert-info outline border-0 mt-4 mb-0 small">
              <i class="fa fa-magic me-1"></i>
              <strong>Self-healing:</strong> if an order leaves the problem state on its own
              (recovers to COMPLETED), the next refresh detects it and closes the case
              automatically. You will never amend an order that already fixed itself.
            </div>
          </div>
        </div>
      </div>

      <!-- Section 4 — Single amendment (Flow 2) -->
      <div
        :id="sections.amendFlow"
        class="col-12 scroll-anchor"
        :class="{ 'oa-section-collapsed': !isSectionOpen(sections.amendFlow) }"
      >
        <div
          class="oa-section-heading"
          role="button"
          tabindex="0"
          :aria-expanded="isSectionOpen(sections.amendFlow)"
          @click="toggleSection(sections.amendFlow)"
          @keydown.enter.prevent="toggleSection(sections.amendFlow)"
          @keydown.space.prevent="toggleSection(sections.amendFlow)"
        >
          <h4 class="mb-1">Flow 2 — Amending One Order</h4>
          <p class="text-muted mb-0">
            Every fix follows the same three-step safety pattern: open → preview → execute.
          </p>
        </div>
        <div class="card border border-light shadow-sm">
          <div class="card-body p-4">
            <div class="oa-flow">
              <div class="oa-step oa-step--source">
                <div class="oa-step__title">You click a row in the Queue</div>
                <div class="oa-step__desc">
                  The case detail page opens at <code>/order-amendments/&lt;uuid&gt;</code>.
                </div>
              </div>
              <div class="oa-arrow"></div>
              <div class="oa-step">
                <div class="oa-step__title">Portal re-fetches the live order</div>
                <div class="oa-step__desc">
                  The case in the queue is a snapshot. The detail page always pulls the latest state
                  from Order Service so you are looking at reality, not a cached copy.
                </div>
              </div>
              <div class="oa-arrow"></div>
              <div class="oa-step">
                <div class="oa-step__title">You edit a draft</div>
                <div class="oa-step__desc">
                  Add / change / remove products, fix billing details, choose
                  <em>captureOnAmend</em> if the order needs to be charged. Nothing is sent upstream
                  yet — drafts are local until you preview.
                </div>
              </div>
              <div class="oa-arrow"></div>
              <div class="oa-step oa-step--branch">
                <div class="oa-step__title">Preview</div>
                <div class="oa-step__desc">
                  The backend computes a diff (what fields change), totals, warnings, blockers, and
                  a one-time <em>preview token</em>. The modal shows you exactly what will happen.
                </div>
              </div>
              <div class="oa-arrow"></div>
              <div class="oa-step oa-step--branch">
                <div class="oa-step__title">Execute (with confirmation)</div>
                <div class="oa-step__desc">
                  You submit the token. If the order changed in the meantime (someone else edited,
                  or the system updated it) the token is rejected with a "stale preview" message —
                  you re-preview and continue. This stops two operators from overwriting each other.
                </div>
              </div>
              <div class="oa-arrow"></div>
              <div class="oa-step oa-step--store">
                <div class="oa-step__title">Order Service receives the amendment</div>
                <div class="oa-step__desc">
                  The order is updated. An <em>idempotency key</em> is attached so the same call
                  twice never creates duplicate effects.
                </div>
              </div>
              <div class="oa-arrow"></div>
              <div class="oa-step">
                <div class="oa-step__title">Invoice is verified in Odoo</div>
                <div class="oa-step__desc">
                  The portal checks via the existing Odoo client whether the order now exists in
                  Odoo. If yes, success. If no, the result is shown as
                  <span class="badge bg-warning text-dark">partial success</span> — never silent.
                </div>
              </div>
              <div class="oa-arrow"></div>
              <div class="oa-step oa-step--end">
                <div class="oa-step__title">Attempt recorded + audit emitted</div>
                <div class="oa-step__desc">
                  Operator, action, before/after, downstream outcome, force flag, captureOnAmend
                  value — everything is written to the attempt log and forwarded to BigQuery for
                  company-wide search.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Section 5 — Bulk jobs (Flow 3) -->
      <div
        :id="sections.bulkFlow"
        class="col-12 scroll-anchor"
        :class="{ 'oa-section-collapsed': !isSectionOpen(sections.bulkFlow) }"
      >
        <div
          class="oa-section-heading"
          role="button"
          tabindex="0"
          :aria-expanded="isSectionOpen(sections.bulkFlow)"
          @click="toggleSection(sections.bulkFlow)"
          @keydown.enter.prevent="toggleSection(sections.bulkFlow)"
          @keydown.space.prevent="toggleSection(sections.bulkFlow)"
        >
          <h4 class="mb-1">Flow 3 — Bulk Jobs</h4>
          <p class="text-muted mb-0">
            Use bulk when 10+ orders need the same action — typical case: EV Reconcile cancelling
            hundreds of stuck charging orders at once.
          </p>
        </div>
        <div class="card border border-light shadow-sm">
          <div class="card-body p-4">
            <div class="oa-flow oa-flow--horizontal-on-lg">
              <div class="oa-step">
                <div class="oa-step__title">Select rows in the Queue</div>
                <div class="oa-step__desc">
                  Filter to the right tab (e.g. EV Reconcile), tick the rows, pick one action
                  (cancel / complete), write one reason.
                </div>
              </div>
              <div class="oa-arrow"></div>
              <div class="oa-step oa-step--branch">
                <div class="oa-step__title">Bulk job created</div>
                <div class="oa-step__desc">
                  A snapshot of the selection is saved to the database with the chosen action and
                  reason. From this moment, removing orders from the queue does not change the job.
                </div>
              </div>
              <div class="oa-arrow"></div>
              <div class="oa-step oa-step--trigger">
                <div class="oa-step__title">Worker processes in batches of 3</div>
                <div class="oa-step__desc">
                  Three orders run in parallel, then a 100&nbsp;ms pause, then the next three. Each
                  item is re-fetched and re-checked before its individual amendment runs.
                </div>
              </div>
              <div class="oa-arrow"></div>
              <div class="oa-step oa-step--store">
                <div class="oa-step__title">Per-item results stored</div>
                <div class="oa-step__desc">
                  Each item gets its own attempt record with success / failure / reason. The detail
                  page shows live progress (e.g. "65 / 100").
                </div>
              </div>
              <div class="oa-arrow"></div>
              <div class="oa-step oa-step--end">
                <div class="oa-step__title">One notification, retry-failures button</div>
                <div class="oa-step__desc">
                  You get a single notification when the job ends. If some items failed you can
                  retry only those — successes are never re-executed.
                </div>
              </div>
            </div>

            <div class="alert alert-warning outline border-0 mt-4 mb-0 small">
              <i class="fa fa-exclamation-triangle me-1"></i>
              The browser does not loop through orders. Closing the tab or losing wifi does not stop
              the job — it runs server-side and resumes on its own.
            </div>
          </div>
        </div>
      </div>

      <!-- Section 6 — Drive Away (Flow 4) -->
      <div
        :id="sections.driveAwayFlow"
        class="col-12 scroll-anchor"
        :class="{ 'oa-section-collapsed': !isSectionOpen(sections.driveAwayFlow) }"
      >
        <div
          class="oa-section-heading"
          role="button"
          tabindex="0"
          :aria-expanded="isSectionOpen(sections.driveAwayFlow)"
          @click="toggleSection(sections.driveAwayFlow)"
          @keydown.enter.prevent="toggleSection(sections.driveAwayFlow)"
          @keydown.space.prevent="toggleSection(sections.driveAwayFlow)"
        >
          <h4 class="mb-1">Flow 4 — Drive Away</h4>
          <p class="text-muted mb-0">
            A drive-away is a FAILED order that the customer paid for later via SEPA transfer,
            PayPal, etc. — we manually complete the order and create the invoice with the payment
            details.
          </p>
        </div>
        <div class="card border border-light shadow-sm">
          <div class="card-body p-4">
            <div class="oa-flow">
              <div class="oa-step oa-step--source">
                <div class="oa-step__title">The case is classified as Drive Away</div>
                <div class="oa-step__desc">
                  The case detail page shows an "Open Drive-Away Flow" call-to-action for
                  drive-away-classified cases — a guided 4-step page that walks you through the
                  rest.
                </div>
              </div>
              <div class="oa-arrow"></div>
              <div class="oa-step oa-step--branch">
                <div class="oa-step__title">You pick the payment object</div>
                <div class="oa-step__desc">
                  Choose one of the hardcoded defaults: SEPA bank transfer to the ryd account,
                  PayPal, etc. Enter the customer's payment reference where applicable.
                </div>
              </div>
              <div class="oa-arrow"></div>
              <div class="oa-step">
                <div class="oa-step__title">Preview</div>
                <div class="oa-step__desc">
                  The diff shows: order will move to COMPLETED_MANUALLY, invoice will be created
                  with the payment object you chose.
                </div>
              </div>
              <div class="oa-arrow"></div>
              <div class="oa-step oa-step--store">
                <div class="oa-step__title">Execute → order completed</div>
                <div class="oa-step__desc">
                  The amendment is sent. The selected payment object and any bank details you
                  entered are saved in the audit store, so we always know who chose what.
                </div>
              </div>
              <div class="oa-arrow"></div>
              <div class="oa-step oa-step--end">
                <div class="oa-step__title">Invoice generation runs</div>
                <div class="oa-step__desc">
                  The portal asks Odoo to create the invoice with the chosen payment object — using
                  the existing contract, no Odoo changes needed.
                </div>
              </div>
            </div>
            <div class="alert alert-info outline border-0 mt-4 mb-0 small">
              <i class="fa fa-info-circle me-1"></i>
              This is the workaround that
              <strong
                >unblocks the drive-away process without waiting for the RP-4239 backend fix</strong
              >. Order Service and Odoo see standard traffic; the responsibility for choosing the
              payment object lives in the portal, with full audit.
            </div>
          </div>
        </div>
      </div>

      <!-- Section 7 — Refund & cancel (Flow 5) -->
      <div
        :id="sections.refundFlow"
        class="col-12 scroll-anchor"
        :class="{ 'oa-section-collapsed': !isSectionOpen(sections.refundFlow) }"
      >
        <div
          class="oa-section-heading"
          role="button"
          tabindex="0"
          :aria-expanded="isSectionOpen(sections.refundFlow)"
          @click="toggleSection(sections.refundFlow)"
          @keydown.enter.prevent="toggleSection(sections.refundFlow)"
          @keydown.space.prevent="toggleSection(sections.refundFlow)"
        >
          <h4 class="mb-1">Flow 5 — Refund &amp; Cancel</h4>
          <p class="text-muted mb-0">
            Admin only. The portal does <strong>not</strong> push money back to the customer — the
            PSP (PayPal, Amazon Pay, …) is operated manually. The portal tracks what you did so
            nothing falls through the cracks.
          </p>
        </div>
        <div class="card border border-light shadow-sm">
          <div class="card-body p-4">
            <div class="oa-flow">
              <div class="oa-step oa-step--source">
                <div class="oa-step__title">Admin opens the order detail page</div>
                <div class="oa-step__desc">
                  Refund &amp; Cancel is gated to <code>pay_operations_admin</code>.
                </div>
              </div>
              <div class="oa-arrow"></div>
              <div class="oa-step oa-step--branch">
                <div class="oa-step__title">Pick a reason category + confirm last-8 of UUID</div>
                <div class="oa-step__desc">
                  Categories: Fraud, Gift card, Wrong pump, Goodwill, Test, Other. Last-8 confirm
                  prevents wrong-order mistakes.
                </div>
              </div>
              <div class="oa-arrow"></div>
              <div class="oa-step">
                <div class="oa-step__title">Order is cancelled in Order Service</div>
                <div class="oa-step__desc">
                  Move to CANCELLED. The amendment-attempt record carries the reason, category,
                  operator.
                </div>
              </div>
              <div class="oa-arrow"></div>
              <div class="oa-step oa-step--store">
                <div class="oa-step__title">Refund-tracking record opens (status: pending)</div>
                <div class="oa-step__desc">
                  The order detail page shows
                  <span class="badge bg-warning text-dark">refund pending — manual PSP action</span
                  >.
                </div>
              </div>
              <div class="oa-arrow"></div>
              <div class="oa-step oa-step--trigger">
                <div class="oa-step__title">You refund manually on the PSP platform</div>
                <div class="oa-step__desc">
                  Go to PayPal / Amazon Pay / etc. and execute the refund there.
                </div>
              </div>
              <div class="oa-arrow"></div>
              <div class="oa-step oa-step--end">
                <div class="oa-step__title">You update the tracker</div>
                <div class="oa-step__desc">
                  Set the status to <code>in_progress</code>, then <code>refunded</code> (or
                  <code>rejected</code>), add a comment. Only then does the badge change.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Section 7b — Odoo Payment Update (Flow 6) -->
      <div
        :id="sections.odooPaymentFlow"
        class="col-12 scroll-anchor"
        :class="{ 'oa-section-collapsed': !isSectionOpen(sections.odooPaymentFlow) }"
      >
        <div
          class="oa-section-heading"
          role="button"
          tabindex="0"
          :aria-expanded="isSectionOpen(sections.odooPaymentFlow)"
          @click="toggleSection(sections.odooPaymentFlow)"
          @keydown.enter.prevent="toggleSection(sections.odooPaymentFlow)"
          @keydown.space.prevent="toggleSection(sections.odooPaymentFlow)"
        >
          <h4 class="mb-1">Flow 6 — Odoo Payment Update</h4>
          <p class="text-muted mb-0">
            Use this when an invoice in Odoo is missing its payment — the classic
            <code>body.payment is null</code> failure after a drive-away. The page walks you through
            preparing the invoice and attaching the payment object.
          </p>
        </div>
        <div class="card border border-light shadow-sm">
          <div class="card-body p-4">
            <div class="oa-flow">
              <div class="oa-step oa-step--source">
                <div class="oa-step__title">Open Odoo Payment Update and look up the order</div>
                <div class="oa-step__desc">
                  Paste the order UUID. The page loads the live order and shows its context — state,
                  amount, partner — so you can confirm you have the right one.
                </div>
              </div>
              <div class="oa-arrow"></div>
              <div class="oa-step oa-step--trigger">
                <div class="oa-step__title">Ask Odoo to prepare the invoice</div>
                <div class="oa-step__desc">
                  One click triggers invoice generation in Order Service. The request is written to
                  the audit log before you see a success message — no unaudited mutations.
                </div>
              </div>
              <div class="oa-arrow"></div>
              <div class="oa-step">
                <div class="oa-step__title">The portal waits for Odoo</div>
                <div class="oa-step__desc">
                  Odoo produces its queue message asynchronously, usually within 30 seconds. The
                  page polls every 5 seconds; you can extend the wait, check immediately, or stop.
                </div>
              </div>
              <div class="oa-arrow"></div>
              <div class="oa-step oa-step--branch">
                <div class="oa-step__title">Fill the payment object</div>
                <div class="oa-step__desc">
                  Payment type (SEPA transfer, PayPal, …), bank details, amount, currency, and the
                  customer's payment reference. Defaults are offered; every value you enter is
                  audited.
                </div>
              </div>
              <div class="oa-arrow"></div>
              <div class="oa-step oa-step--store">
                <div class="oa-step__title">Review and send</div>
                <div class="oa-step__desc">
                  The portal creates a new Odoo queue message carrying your payment object. The
                  result card shows the queue message ID and the invoice status.
                </div>
              </div>
              <div class="oa-arrow"></div>
              <div class="oa-step oa-step--end">
                <div class="oa-step__title">Audit Logs card</div>
                <div class="oa-step__desc">
                  Every invoice-generation trigger and payment update for the order is listed on the
                  page — immutable, with operator and timestamp.
                </div>
              </div>
            </div>
            <div class="alert alert-info outline border-0 mt-4 mb-0 small">
              <i class="fa fa-key me-1"></i>
              This page requires <code>pay_operations_agent</code> or
              <code>pay_operations_admin</code> — customer-support roles do not unlock it.
            </div>
          </div>
        </div>
      </div>

      <!-- Section 7c — Manual Assignment (Flow 7) -->
      <div
        :id="sections.assignmentFlow"
        class="col-12 scroll-anchor"
        :class="{ 'oa-section-collapsed': !isSectionOpen(sections.assignmentFlow) }"
      >
        <div
          class="oa-section-heading"
          role="button"
          tabindex="0"
          :aria-expanded="isSectionOpen(sections.assignmentFlow)"
          @click="toggleSection(sections.assignmentFlow)"
          @keydown.enter.prevent="toggleSection(sections.assignmentFlow)"
          @keydown.space.prevent="toggleSection(sections.assignmentFlow)"
        >
          <h4 class="mb-1">Flow 7 — Manual Assignment</h4>
          <p class="text-muted mb-0">
            Attach a list of orders to an incident, or override a wrong automatic classification —
            in one batched, conflict-safe run instead of one order at a time.
          </p>
        </div>
        <div class="card border border-light shadow-sm">
          <div class="card-body p-4">
            <div class="oa-flow">
              <div class="oa-step oa-step--source">
                <div class="oa-step__title">Choose the target</div>
                <div class="oa-step__desc">
                  Either an open incident (pick from the list) or a classification (Standard,
                  Overshoot, EV Reconcile, Drive Away). Write a reason of at least 10 characters —
                  it lands in the audit trail.
                </div>
              </div>
              <div class="oa-arrow"></div>
              <div class="oa-step">
                <div class="oa-step__title">Paste the order UUIDs</div>
                <div class="oa-step__desc">
                  Up to 500 per session, one per line or comma-separated. Duplicates are removed
                  automatically; malformed UUIDs are rejected without touching Order Service.
                </div>
              </div>
              <div class="oa-arrow"></div>
              <div class="oa-step oa-step--branch">
                <div class="oa-step__title">Review before submitting</div>
                <div class="oa-step__desc">
                  You see exactly which UUIDs will be processed. Nothing has happened yet — this is
                  the moment to catch a wrong paste.
                </div>
              </div>
              <div class="oa-arrow"></div>
              <div class="oa-step oa-step--trigger">
                <div class="oa-step__title">Batched processing with per-order results</div>
                <div class="oa-step__desc">
                  Each order is looked up live and committed individually. Results per order:
                  processed, conflict (already in the incident / already classified), or failed (not
                  found, lookup error) — conflicts are reported, never silently overwritten.
                </div>
              </div>
              <div class="oa-arrow"></div>
              <div class="oa-step oa-step--end">
                <div class="oa-step__title">Result page with a shareable link</div>
                <div class="oa-step__desc">
                  The session (totals + per-order buckets) is stored and has its own URL — hand it
                  to a colleague instead of describing what you did.
                </div>
              </div>
            </div>
            <div class="alert alert-info outline border-0 mt-4 mb-0 small">
              <i class="fa fa-lock me-1"></i>
              A manually set classification is <strong>locked</strong> — the automatic refresh will
              not overwrite it until the lock is explicitly cleared.
            </div>
          </div>
        </div>
      </div>

      <!-- Section 8 — Queue tabs guide -->
      <div
        :id="sections.tabs"
        class="col-12 scroll-anchor"
        :class="{ 'oa-section-collapsed': !isSectionOpen(sections.tabs) }"
      >
        <div
          class="oa-section-heading"
          role="button"
          tabindex="0"
          :aria-expanded="isSectionOpen(sections.tabs)"
          @click="toggleSection(sections.tabs)"
          @keydown.enter.prevent="toggleSection(sections.tabs)"
          @keydown.space.prevent="toggleSection(sections.tabs)"
        >
          <h4 class="mb-1">Queue Tabs — When To Use Which</h4>
          <p class="text-muted mb-0">
            Tabs are mutually exclusive. One case lives in exactly one classification (plus optional
            Incidents membership).
          </p>
        </div>
        <div class="card border border-light shadow-sm">
          <div class="card-body p-0">
            <div class="table-responsive">
              <table class="table table-hover oa-tabs-table mb-0">
                <thead>
                  <tr>
                    <th style="width: 16%">Tab</th>
                    <th style="width: 10%">Share</th>
                    <th style="width: 22%">What lands here</th>
                    <th style="width: 24%">Typical action</th>
                    <th>Notes for you</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="tab in tabs" :key="tab.key">
                    <td class="fw-semibold align-top">{{ tab.label }}</td>
                    <td class="align-top text-muted">{{ tab.share }}</td>
                    <td class="align-top">{{ tab.lands }}</td>
                    <td class="align-top">{{ tab.action }}</td>
                    <td class="align-top text-muted">{{ tab.notes }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <!-- Section 9 — Roles -->
      <div
        :id="sections.roles"
        class="col-12 scroll-anchor"
        :class="{ 'oa-section-collapsed': !isSectionOpen(sections.roles) }"
      >
        <div
          class="oa-section-heading"
          role="button"
          tabindex="0"
          :aria-expanded="isSectionOpen(sections.roles)"
          @click="toggleSection(sections.roles)"
          @keydown.enter.prevent="toggleSection(sections.roles)"
          @keydown.space.prevent="toggleSection(sections.roles)"
        >
          <h4 class="mb-1">Who Can Do What</h4>
          <p class="text-muted mb-0">
            Both the Customer Support roles and the Pay Operations roles map to the same permission
            matrix. The backend checks every action regardless of what the UI shows.
          </p>
        </div>
        <div class="card border border-light shadow-sm">
          <div class="card-body p-0">
            <div class="table-responsive">
              <table class="table table-bordered table-sm oa-roles-table mb-0">
                <thead>
                  <tr>
                    <th>Action</th>
                    <th class="text-center">Viewer</th>
                    <th class="text-center">Agent</th>
                    <th class="text-center">Admin</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in rolesMatrix" :key="row.action">
                    <td>{{ row.action }}</td>
                    <td class="text-center">
                      <span :class="rolePillClass(row.viewer)">{{ row.viewer }}</span>
                    </td>
                    <td class="text-center">
                      <span :class="rolePillClass(row.agent)">{{ row.agent }}</span>
                    </td>
                    <td class="text-center">
                      <span :class="rolePillClass(row.admin)">{{ row.admin }}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div class="card-footer bg-light small text-muted">
            "Viewer" = pay_operations_viewer. "Agent" = pay_operations_agent. "Admin" =
            pay_operations_admin.
          </div>
        </div>
      </div>

      <!-- Section 10 — State transitions -->
      <div
        :id="sections.states"
        class="col-12 scroll-anchor"
        :class="{ 'oa-section-collapsed': !isSectionOpen(sections.states) }"
      >
        <div
          class="oa-section-heading"
          role="button"
          tabindex="0"
          :aria-expanded="isSectionOpen(sections.states)"
          @click="toggleSection(sections.states)"
          @keydown.enter.prevent="toggleSection(sections.states)"
          @keydown.space.prevent="toggleSection(sections.states)"
        >
          <h4 class="mb-1">Order State Transitions</h4>
          <p class="text-muted mb-0">
            The backend enforces every transition. If a state is missing from the "From" list for an
            action, you cannot trigger it — the action button is disabled.
          </p>
        </div>
        <div class="card border border-light shadow-sm">
          <div class="card-body p-0">
            <div class="table-responsive">
              <table class="table table-hover oa-states-table mb-0">
                <thead>
                  <tr>
                    <th>Action</th>
                    <th>Allowed from states</th>
                    <th>Moves to</th>
                    <th>Role</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="t in transitions" :key="t.action">
                    <td class="fw-semibold">{{ t.action }}</td>
                    <td>
                      <span v-for="s in t.from" :key="s" class="badge oa-state-badge me-1 mb-1">
                        {{ s }}
                      </span>
                    </td>
                    <td>
                      <span class="badge oa-state-badge oa-state-badge--target">
                        {{ t.to }}
                      </span>
                    </td>
                    <td class="small">{{ t.role }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <!-- Section 11 — FAQ -->
      <div
        :id="sections.faq"
        class="col-12 scroll-anchor"
        :class="{ 'oa-section-collapsed': !isSectionOpen(sections.faq) }"
      >
        <div
          class="oa-section-heading"
          role="button"
          tabindex="0"
          :aria-expanded="isSectionOpen(sections.faq)"
          @click="toggleSection(sections.faq)"
          @keydown.enter.prevent="toggleSection(sections.faq)"
          @keydown.space.prevent="toggleSection(sections.faq)"
        >
          <h4 class="mb-1">Common Situations &amp; FAQ</h4>
          <p class="text-muted mb-0">
            Real things that come up. If yours is missing, add it — this is a living document.
          </p>
        </div>
        <div class="accordion oa-faq" id="oaFaq">
          <div v-for="(faq, idx) in faqs" :key="faq.q" class="accordion-item">
            <h2 class="accordion-header" :id="`oa-faq-h-${idx}`">
              <button
                class="accordion-button"
                :class="{ collapsed: openFaqIndex !== idx }"
                type="button"
                :aria-expanded="openFaqIndex === idx"
                :aria-controls="`oa-faq-c-${idx}`"
                @click="toggleFaq(idx)"
              >
                <span class="oa-faq-number">{{ String(idx + 1).padStart(2, '0') }}</span>
                <span class="oa-faq-question">{{ faq.q }}</span>
              </button>
            </h2>
            <div
              :id="`oa-faq-c-${idx}`"
              class="accordion-collapse"
              :class="{ show: openFaqIndex === idx }"
              :aria-labelledby="`oa-faq-h-${idx}`"
              v-show="openFaqIndex === idx"
            >
              <div class="accordion-body">
                <div class="oa-faq-answer">
                  <span class="oa-faq-answer-icon">
                    <i class="fa fa-lightbulb-o" aria-hidden="true"></i>
                  </span>
                  <p class="mb-0">{{ faq.a }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-12 text-center text-muted small py-3">
        <i class="fa fa-book me-1"></i>
        Engineering documentation lives in <code>rp-integrations/docs/2026-08-ryd-pay-operations/order-amendment</code>; the
        original concept in
        <code>project-descriptions/ryd-portal-features/order-amedement-process/concept-v2</code>.
      </div>
    </div>
  </div>
</template>

<script setup>
import { nextTick, onBeforeUnmount, onMounted, ref } from 'vue';

defineOptions({ name: 'OrderAmendmentsGuide' });

const openFaqIndex = ref(null);

const sections = {
  useCases: 'oa-use-cases',
  threePages: 'oa-three-pages',
  glossary: 'oa-glossary',
  queueFlow: 'oa-queue-flow',
  amendFlow: 'oa-amend-flow',
  bulkFlow: 'oa-bulk-flow',
  driveAwayFlow: 'oa-driveaway-flow',
  refundFlow: 'oa-refund-flow',
  odooPaymentFlow: 'oa-odoo-payment-flow',
  assignmentFlow: 'oa-assignment-flow',
  tabs: 'oa-tabs',
  roles: 'oa-roles',
  states: 'oa-states',
  faq: 'oa-faq',
};

const tableOfContents = [
  {
    id: sections.useCases,
    title: 'Which Page for Which Case',
    icon: 'fa-map-signs',
    hint: 'Your situation → the right menu item.',
  },
  {
    id: sections.threePages,
    title: 'The Working Pages',
    icon: 'fa-th-large',
    hint: 'Queue, assignment, Odoo payment, incidents, audit.',
  },
  {
    id: sections.glossary,
    title: 'Vocabulary',
    icon: 'fa-book',
    hint: 'Key amendment terms in plain language.',
  },
  {
    id: sections.queueFlow,
    title: 'Flow 1 — Queue Ingestion',
    icon: 'fa-sign-in',
    hint: 'How problem orders become cases.',
  },
  {
    id: sections.amendFlow,
    title: 'Flow 2 — Amending One Order',
    icon: 'fa-edit',
    hint: 'Draft, preview, execute, and audit.',
  },
  {
    id: sections.bulkFlow,
    title: 'Flow 3 — Bulk Jobs',
    icon: 'fa-tasks',
    hint: 'Apply one action to many orders.',
  },
  {
    id: sections.driveAwayFlow,
    title: 'Flow 4 — Drive Away',
    icon: 'fa-car',
    hint: 'Complete the order and create its invoice.',
  },
  {
    id: sections.refundFlow,
    title: 'Flow 5 — Refund & Cancel',
    icon: 'fa-undo',
    hint: 'Cancel, refund manually, and track completion.',
  },
  {
    id: sections.odooPaymentFlow,
    title: 'Flow 6 — Odoo Payment Update',
    icon: 'fa-credit-card',
    hint: 'Attach a payment object to an Odoo invoice.',
  },
  {
    id: sections.assignmentFlow,
    title: 'Flow 7 — Manual Assignment',
    icon: 'fa-tags',
    hint: 'Batch-attach orders to incidents or classifications.',
  },
  {
    id: sections.tabs,
    title: 'Queue Tabs Guide',
    icon: 'fa-folder-open-o',
    hint: 'What each operational queue contains.',
  },
  {
    id: sections.roles,
    title: 'Roles Matrix',
    icon: 'fa-users',
    hint: 'Viewer, agent, and admin permissions.',
  },
  {
    id: sections.states,
    title: 'State Transitions',
    icon: 'fa-exchange',
    hint: 'Allowed source and target order states.',
  },
  {
    id: sections.faq,
    title: 'Common Situations & FAQ',
    icon: 'fa-question-circle',
    hint: 'Troubleshooting and operational answers.',
  },
];

// Which page for which case — the decision table is open by default; everything
// else starts collapsed so the page reads as an index.
const openSections = ref({ [sections.useCases]: true });

function isSectionOpen(sectionId) {
  return !!openSections.value[sectionId];
}

function toggleSection(sectionId) {
  openSections.value[sectionId] = !isSectionOpen(sectionId);
}

async function openAndScrollToSection(sectionId) {
  openSections.value[sectionId] = true;
  await nextTick();
  document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  window.history.replaceState(null, '', `#${sectionId}`);
}

async function openSectionFromHash() {
  const sectionId = window.location.hash.replace(/^#/, '');
  if (!sectionId || !Object.values(sections).includes(sectionId)) return;
  await openAndScrollToSection(sectionId);
}

onMounted(async () => {
  await openSectionFromHash();
  window.addEventListener('hashchange', openSectionFromHash);
});

onBeforeUnmount(() => window.removeEventListener('hashchange', openSectionFromHash));

const useCaseGroups = [
  {
    label: 'Fix One Order',
    icon: 'fa-wrench',
    items: [
      {
        situation: 'A problem order needs a decision — complete or cancel?',
        target: 'Queue › Standard',
        icon: 'fa-list',
        to: { name: 'pay-operations-order-amendments', query: { tab: 'standard' } },
        steps:
          'Open the case, read the error code and evidence, then preview Complete Manually or Cancel Manually and execute.',
        hint: 'The action panel only offers what the order state allows.',
      },
      {
        situation: 'Pump dispensed more than the pre-authorized amount (overshoot)',
        target: 'Queue › Overshoot',
        icon: 'fa-arrow-up',
        to: { name: 'pay-operations-order-amendments', query: { tab: 'overshoot' } },
        steps:
          'Open the case — Complete Manually is preselected. Check the pre-auth vs captured evidence and confirm.',
        hint: 'You confirm, you do not decide: historically 99.8% complete.',
      },
      {
        situation: 'The order is not in the queue (too old, or never flagged)',
        target: 'Queue › UUID Filter',
        icon: 'fa-search',
        to: { name: 'pay-operations-order-amendments' },
        steps:
          'Paste the UUID into the queue filter, or open /pay-operations/order-amendments/<uuid> directly — any order can be amended ad hoc.',
        hint: 'Refresh only ingests problem orders from the last 90 days; ad-hoc always works.',
      },
    ],
  },
  {
    label: 'Fix Many at Once',
    icon: 'fa-tasks',
    items: [
      {
        situation: 'Stuck Plugsurfing EV charging sessions',
        target: 'Queue › EV Reconcile',
        icon: 'fa-bolt',
        to: { name: 'pay-operations-order-amendments', query: { tab: 'ev_reconcile' } },
        steps:
          'Select all eligible rows and run one bulk Cancel Manually with a shared reason. Monitor progress in the bulk-job page.',
        hint: 'The job runs server-side — closing the tab does not stop it.',
      },
      {
        situation: 'Many orders share one root cause (outage, partner bug)',
        target: 'Incidents',
        icon: 'fa-exclamation-circle',
        to: { name: 'pay-operations-order-amendments-incidents' },
        steps:
          'Create a named incident, then attach the orders via Manual Assignment. Bulk actions can be scoped to the incident.',
        hint: 'One order can belong to several incidents.',
      },
      {
        situation: 'Attach a UUID list to an incident, or fix a wrong classification',
        target: 'Manual Assignment',
        icon: 'fa-tags',
        to: { name: 'pay-operations-manual-assignment' },
        steps:
          'Choose the target, paste up to 500 UUIDs, review, submit. Conflicts are reported per order, never overwritten.',
        hint: 'Manual classifications are locked against automatic refresh.',
      },
    ],
  },
  {
    label: 'Invoices & Money',
    icon: 'fa-credit-card',
    items: [
      {
        situation: 'Customer drove away without completing payment',
        target: 'Case Detail › Drive-Away Flow',
        icon: 'fa-car',
        to: { name: 'pay-operations-order-amendments', query: { tab: 'standard' } },
        targetHint: 'Open the case; drive-away cases show the guided flow.',
        steps:
          'Follow the 4 steps: receipt data → complete the order → choose the payment object → create the invoice.',
        hint: 'Agent or admin role required.',
      },
      {
        situation: 'Odoo invoice is missing its payment (body.payment is null)',
        target: 'Odoo Payment Update',
        icon: 'fa-credit-card',
        to: { name: 'pay-operations-odoo-payment-update' },
        steps:
          'Look up the order, ask Odoo to prepare the invoice, wait for readiness, then attach the payment object and send.',
        hint: 'Requires a pay_operations agent or admin role.',
      },
      {
        situation: 'Money has to go back to the customer',
        target: 'Case Detail › Refund & Cancel',
        icon: 'fa-undo',
        to: { name: 'pay-operations-order-amendments', query: { tab: 'all' } },
        targetHint: 'Admin only — opens from the case action panel.',
        steps:
          'Cancel with a reason category and UUID confirmation, execute the refund manually on the PSP, then update the refund tracker.',
        hint: 'The portal never moves money itself.',
      },
    ],
  },
  {
    label: 'Trace & Verify',
    icon: 'fa-history',
    items: [
      {
        situation: 'Check who changed an order, when, and why',
        target: 'Audit',
        icon: 'fa-history',
        to: { name: 'pay-operations-order-amendments-audit' },
        targetHint: 'Admin only. All roles see the per-case timeline instead.',
        steps: 'Filter by operator, action, order UUID, or date range.',
        hint: 'Every attempt is recorded — successes and failures alike.',
      },
    ],
  },
];

const pages = [
  {
    title: 'Queue',
    routeName: 'pay-operations-order-amendments',
    icon: 'fa-list',
    iconClass: 'oa-icon-wrap--primary text-primary',
    summary:
      'The daily work surface. All cases that need attention, with tabs, filters, and bulk-action controls.',
    bullets: [
      'Tabs: All, Standard, Overshoot, EV Reconcile, Incidents, Resolved',
      'Filter by UUID, partner, assignee, classification, status',
      'Click a row → case detail page with editor, preview, execute, timeline',
    ],
  },
  {
    title: 'Manual Assignment',
    routeName: 'pay-operations-manual-assignment',
    icon: 'fa-tags',
    iconClass: 'oa-icon-wrap--primary text-primary',
    roleNote: 'Agent+',
    summary:
      'Attach many orders to an incident or override automatic classifications in one batched, conflict-safe run.',
    bullets: [
      '4-step wizard: target → UUIDs → review → result',
      'Up to 500 UUIDs per session, deduplicated automatically',
      'Per-order results: processed, conflict, or failed — with a shareable session link',
    ],
  },
  {
    title: 'Odoo Payment Update',
    routeName: 'pay-operations-odoo-payment-update',
    icon: 'fa-credit-card',
    iconClass: 'oa-icon-wrap--primary text-primary',
    roleNote: 'Agent+',
    summary:
      'Attach a payment object to an Odoo invoice when the automatic path could not — the fix for "body.payment is null".',
    bullets: [
      'Guided phases: look up → prepare invoice → fill payment → result',
      'Polls Odoo readiness automatically (extendable wait)',
      'Every trigger and update is audited before success is reported',
    ],
  },
  {
    title: 'Incidents',
    routeName: 'pay-operations-order-amendments-incidents',
    icon: 'fa-bell',
    iconClass: 'oa-icon-wrap--alert text-danger',
    summary:
      'Named, structured groupings of related cases — replaces the free-text comment column with the 5 spellings of "Viktor".',
    bullets: [
      'Viewers can read incidents; agents and admins create / rename / close them',
      'One order may belong to multiple incidents',
      'Bulk actions can be scoped to one incident at a time',
    ],
  },
  {
    title: 'Audit',
    routeName: 'pay-operations-order-amendments-audit',
    icon: 'fa-history',
    iconClass: 'oa-icon-wrap--muted text-secondary',
    roleNote: 'Admin',
    summary:
      'Admin-only global log. Every amendment (success and failure) with operator, before/after, reason, downstream outcomes.',
    bullets: [
      'Filter by operator, action, date, order UUID',
      'Reads from the immutable amendment-attempt records',
      'Mirrored to BigQuery for company-wide search',
    ],
  },
];

const glossary = [
  {
    term: 'Order',
    meaning:
      'The single source of truth for what the customer did. Lives in Order Service. The portal always re-fetches it live before any action — we never trust a cached copy for mutations.',
  },
  {
    term: 'Case',
    aka: 'amendment_cases',
    meaning:
      'A row in the Queue. It says "this order needs attention" and carries the classification, status (open / assigned / in_progress / blocked / resolved), assignee, days open, and shortcuts to the data needed to triage. One case per order.',
  },
  {
    term: 'Draft',
    meaning:
      'The proposed change you are typing into the editor before you preview. Drafts are local — they never reach Order Service.',
  },
  {
    term: 'Attempt',
    aka: 'amendment_attempts',
    meaning:
      'An immutable record of one execution: who did what, the exact payload, the before/after state, the result, and any downstream effects (invoice, refund flag). One amendment = one attempt. Bulk jobs produce one attempt per item.',
  },
  {
    term: 'Bulk job',
    meaning:
      'A server-side runner that applies one action to many orders. You snapshot the selection, pick the action, write the reason — the portal does the rest in batches of 3 with full traceability.',
  },
  {
    term: 'Incident',
    meaning:
      'A named container for cases that share a root cause (e.g. "Tokheim outage 2026-05-12"). Replaces 5 spellings of "Viktor" in a comments column. Many-to-many with cases.',
  },
  {
    term: 'Refund tracker',
    meaning:
      'A separate record for orders where money still needs to flow back via a PSP. Statuses: pending, in_progress, refunded, rejected. The portal does not execute PSP refunds — it tracks them so nothing is forgotten.',
  },
  {
    term: 'Classification',
    meaning:
      'The workflow tag computed by the backend: Standard, Overshoot, EV Reconcile, Drive Away. Each one has a default action and different evidence shown in the detail page.',
  },
  {
    term: 'Preview token',
    meaning:
      'A one-time receipt the backend gives you after computing the diff. You submit it back when executing. If the order changed in the meantime, the token is stale and the execute is rejected — you re-preview. Prevents two operators from overwriting each other.',
  },
  {
    term: 'Idempotency key',
    meaning:
      'A unique tag attached to every execute call. If the same call arrives twice (network hiccup, double click) the upstream system returns the original result instead of doing it twice.',
  },
  {
    term: 'captureOnAmend',
    meaning:
      "A toggle on the editor. When ON, the amendment also charges the customer's payment method. When OFF, it only changes the order data. The chosen value is recorded in the audit trail.",
  },
  {
    term: 'forceComplete',
    meaning:
      'An admin-only escape hatch that completes an order even from states the standard transition table forbids. Always recorded with a force flag in the audit log.',
  },
  {
    term: 'Self-heal',
    meaning:
      'When a refresh detects that a case no longer matches the problem-state filter (because the order recovered on its own) the case is auto-closed. You will never amend an already-recovered order.',
  },
];

const tabs = [
  {
    key: 'all',
    label: 'All / Active',
    share: 'all open',
    lands: 'Every case that is not Resolved.',
    action: '—',
    notes: 'Default landing tab — gives the full picture.',
  },
  {
    key: 'standard',
    label: 'Standard',
    share: '~43%',
    lands: 'PROBLEM / FAILED / REJECTED orders that do not match Overshoot or EV.',
    action: 'Operator decides (cancel vs complete)',
    notes: 'Use the error-code reference table to pick the right action.',
  },
  {
    key: 'overshoot',
    label: 'Overshoot',
    share: '~17%',
    lands: 'PROBLEM + stateHistory shows actionCode 11012003 (authorization vs capture mismatch).',
    action: 'completeManually pre-selected',
    notes: 'You confirm; you do not have to decide. Historically 978/980 complete.',
  },
  {
    key: 'ev',
    label: 'EV Reconcile',
    share: '~15%',
    lands: 'Plugsurfing partner + EV_CHARGE + PROBLEM state.',
    action: 'cancelManually, bulk-first',
    notes: 'Use the "select all eligible" + auto-reason workflow.',
  },
  {
    key: 'incidents',
    label: 'Incidents',
    share: '~15%',
    lands: 'Any case with at least one incident label.',
    action: 'Mixed — depends on incident playbook',
    notes: 'Replaces the per-incident tabs from the sheet (TOKHEIM, SB, Viktor).',
  },
  {
    key: 'resolved',
    label: 'Resolved',
    share: 'history',
    lands: 'Closed cases.',
    action: '—',
    notes: 'Kept out of active tabs so the workload counts are accurate.',
  },
];

const rolesMatrix = [
  { action: 'See queue, case detail, attempts', viewer: 'yes', agent: 'yes', admin: 'yes' },
  { action: 'Refresh from Order Service', viewer: 'no', agent: 'yes', admin: 'yes' },
  { action: 'Assign / unassign cases', viewer: 'no', agent: 'yes', admin: 'yes' },
  { action: 'Preview an amendment', viewer: 'no', agent: 'yes', admin: 'yes' },
  { action: 'Execute completeManually / cancelManually', viewer: 'no', agent: 'yes', admin: 'yes' },
  { action: 'Bulk cancel', viewer: 'no', agent: 'yes', admin: 'yes' },
  { action: 'Drive Away flow', viewer: 'no', agent: 'yes', admin: 'yes' },
  { action: 'forceCompleteManually (escape hatch)', viewer: 'no', agent: 'no', admin: 'yes' },
  { action: 'refundAndCancelManually', viewer: 'no', agent: 'no', admin: 'yes' },
  { action: 'Update refund tracker', viewer: 'no', agent: 'yes', admin: 'yes' },
  {
    action: 'Manage incidents (create / rename / close)',
    viewer: 'no',
    agent: 'yes',
    admin: 'yes',
  },
  { action: 'Manual assignment sessions', viewer: 'no', agent: 'yes', admin: 'yes' },
  {
    action: 'Odoo Payment Update / trigger invoice generation',
    viewer: 'no',
    agent: 'yes',
    admin: 'yes',
  },
  { action: 'Global audit page', viewer: 'no', agent: 'no', admin: 'yes' },
];

const transitions = [
  {
    action: 'completeManually',
    from: ['PROBLEM', 'CANCELLED', 'FAILED'],
    to: 'COMPLETED_MANUALLY',
    role: 'Agent',
  },
  {
    action: 'cancelManually',
    from: ['PROBLEM', 'FAILED', 'REJECTED'],
    to: 'CANCELLED',
    role: 'Agent',
  },
  {
    action: 'forceCompleteManually',
    from: ['COMPLETED_MANUALLY', 'CANCELLED', 'PROBLEM', 'FAILED'],
    to: 'COMPLETED_MANUALLY',
    role: 'Admin',
  },
  {
    action: 'refundAndCancelManually',
    from: ['COMPLETED', 'COMPLETED_MANUALLY', 'READY_FOR_INVOICE'],
    to: 'CANCELLED',
    role: 'Admin',
  },
];

const faqs = [
  {
    q: 'The queue shows an order I think is already fine — what happened?',
    a: 'It probably recovered after the last scheduled refresh. Click "Refresh" on the queue (or open the case and refresh that one) — the self-heal logic will close it if Order Service confirms it is no longer in a problem state.',
  },
  {
    q: 'I clicked Execute and got "Preview is stale, please re-preview" — did anything change?',
    a: 'No. The order changed in Order Service between your Preview and your Execute (another operator, scheduled task, or a real customer interaction). Nothing was sent. Re-open Preview to see the new diff, confirm, and execute again.',
  },
  {
    q: 'The execute result says "order updated but invoice failed" — what now?',
    a: 'The order was amended successfully but Odoo did not produce the invoice. The portal makes this a loud partial-success state on purpose. Check the Odoo side or escalate; the order itself is in the new state already, so you do not need to re-amend.',
  },
  {
    q: 'I want to refund a customer immediately — can the portal do it?',
    a: 'No. The portal triggers the cancellation and opens a refund-tracker entry, but the PSP refund (PayPal, Amazon Pay, …) is done manually by you on the provider platform. After you finish there, update the tracker so the case page stops showing "refund pending".',
  },
  {
    q: 'My bulk job has 12 failures — should I redo the whole batch?',
    a: 'No. Open the bulk job detail page and click Retry failures. The 88 successful items are never re-executed (idempotency); only the 12 failures run again.',
  },
  {
    q: 'Does the portal ever delete data?',
    a: 'No. Cases, attempts, bulk jobs, incidents and refund records are kept indefinitely. There is no purge job — the audit trail is the system of record for compliance.',
  },
  {
    q: 'I closed my browser during a bulk job — did it stop?',
    a: 'No. The browser only displays progress. The actual execution runs server-side and continues regardless. When you come back, open the bulk job detail page to see the final result.',
  },
  {
    q: 'A field on the editor accepts a value Order Service later rejects — what guards that?',
    a: 'For v1 the backend accepts any value (currencies, tax classes are operator-provided) so we do not block the workflow on edge cases. The Preview step runs the same validation Order Service will run, and any rejection shows up in the result with the full diff — no silent failure.',
  },
  {
    q: 'The order I need is not in the queue — can I still amend it?',
    a: 'Yes. The queue only ingests PROBLEM / FAILED / REJECTED orders from the last 90 days, but any order can be opened ad hoc: paste its UUID into the queue filter or open /pay-operations/order-amendments/<uuid> directly. The detail page loads the live order and offers whatever actions its state allows.',
  },
  {
    q: 'An Odoo invoice failed with "body.payment is null" — where do I fix it?',
    a: 'Use the Odoo Payment Update page. It asks Odoo to prepare the invoice, waits until the queue message is ready, and then lets you attach the payment object (payment type, bank details, reference). Both steps are audited. See Flow 6 above for the full walkthrough.',
  },
  {
    q: 'A case is in the wrong tab (wrong classification) — how do I fix it?',
    a: 'Use Manual Assignment with the target "Classification". The new classification is locked so the automatic refresh will not overwrite it. If control should go back to the automatic classifier later, ask the engineering team to clear the lock — the backend supports it, a portal button is on the backlog.',
  },
];

function rolePillClass(value) {
  if (value === 'yes') return 'badge oa-role-pill oa-role-pill--yes';
  return 'badge oa-role-pill oa-role-pill--no';
}

function toggleFaq(index) {
  openFaqIndex.value = openFaqIndex.value === index ? null : index;
}
</script>

<style scoped>
.oa-overview {
  scroll-behavior: smooth;
}

/* Hero --------------------------------------------------------------------- */
.oa-hero-card {
  background:
    radial-gradient(circle at top right, rgba(0, 161, 254, 0.12), transparent 32%),
    linear-gradient(135deg, rgba(248, 250, 255, 0.98), rgba(255, 255, 255, 0.98));
  border: 1px solid rgba(0, 161, 254, 0.1);
}

.oa-eyebrow {
  font-size: 0.72rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  font-weight: 700;
  color: var(--theme-default);
}

.oa-eyebrow-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--theme-default);
}

.oa-hero-copy {
  max-width: 60rem;
  font-size: 1rem;
}

.oa-soft-badge {
  background: rgba(0, 161, 254, 0.1);
  color: var(--theme-default);
  border: 1px solid rgba(0, 161, 254, 0.12);
  font-weight: 600;
}

/* Page index --------------------------------------------------------------- */
.oa-index-card {
  border: 1px solid rgba(15, 23, 42, 0.08);
  box-shadow:
    0 1px 0 rgba(15, 23, 42, 0.02),
    0 8px 22px rgba(15, 23, 42, 0.04);
}

.oa-index-link {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 0.6rem;
  background: #fff;
  padding: 0.85rem 0.95rem;
  color: #172033;
  text-decoration: none;
  transition:
    border-color 0.18s ease,
    box-shadow 0.18s ease,
    transform 0.18s ease;
}

.oa-index-link:hover {
  transform: translateY(-1px);
  border-color: rgba(0, 161, 254, 0.32);
  color: var(--theme-default);
  box-shadow: 0 7px 16px rgba(15, 23, 42, 0.06);
}

.oa-index-icon {
  display: inline-flex;
  width: 2.25rem;
  min-width: 2.25rem;
  height: 2.25rem;
  align-items: center;
  justify-content: center;
  border-radius: 0.55rem;
  background: rgba(0, 161, 254, 0.08);
  color: var(--theme-default);
}

.oa-index-link strong,
.oa-index-link small {
  display: block;
}

.oa-index-link strong {
  margin-bottom: 0.12rem;
  font-size: 0.9rem;
}

.oa-index-link small {
  color: #667085;
  font-size: 0.78rem;
  line-height: 1.4;
}

.oa-index-chevron {
  color: #98a2b3;
  font-size: 0.72rem;
  transition:
    transform 0.15s ease,
    color 0.15s ease;
}

/* The index reflects which sections are currently expanded on the page. */
.oa-index-link--open {
  border-color: rgba(0, 161, 254, 0.35);
  background: rgba(0, 161, 254, 0.04);
}

.oa-index-link--open .oa-index-chevron {
  transform: rotate(90deg);
  color: var(--theme-default);
}

.oa-section-label {
  font-size: 0.7rem;
  letter-spacing: 0.08em;
}

/* Section headings --------------------------------------------------------- */
.oa-section-heading {
  position: relative;
  margin-top: 1rem;
  margin-bottom: 1rem;
  padding: 0.25rem 3rem 0.25rem 0.75rem;
  border-left: 4px solid var(--theme-default);
  border-radius: 0 0.45rem 0.45rem 0;
  cursor: pointer;
  transition:
    background-color 0.18s ease,
    border-color 0.18s ease;
}

.scroll-anchor:nth-of-type(even) > .oa-section-heading {
  border-left-color: var(--theme-secondary);
}

.scroll-anchor:nth-of-type(even) > .oa-section-heading::after {
  border-color: color-mix(in srgb, var(--theme-secondary) 22%, transparent);
  color: var(--theme-secondary);
}

.scroll-anchor:nth-of-type(even) > .oa-section-heading:hover,
.scroll-anchor:nth-of-type(even).oa-section-collapsed > .oa-section-heading {
  background: color-mix(in srgb, var(--theme-secondary) 5%, transparent);
}

.oa-section-heading::after {
  content: '\f077';
  position: absolute;
  top: 50%;
  right: 0.85rem;
  display: flex;
  width: 2rem;
  height: 2rem;
  align-items: center;
  justify-content: center;
  transform: translateY(-50%);
  border: 1px solid rgba(0, 161, 254, 0.18);
  border-radius: 50%;
  background: #fff;
  color: var(--theme-default);
  font-family: FontAwesome;
  font-size: 0.72rem;
  transition: transform 0.18s ease;
}

.oa-section-heading h4 {
  color: var(--theme-default);
}

.scroll-anchor:nth-of-type(even) > .oa-section-heading h4 {
  color: var(--theme-secondary);
}

.oa-section-heading:hover {
  background: rgba(0, 161, 254, 0.035);
}

.oa-section-heading:focus-visible {
  outline: 2px solid var(--theme-default);
  outline-offset: 4px;
}

.oa-section-collapsed > .oa-section-heading {
  margin-bottom: 0;
  background: rgba(0, 161, 254, 0.035);
}

.oa-section-collapsed > .oa-section-heading::after {
  content: '\f078';
}

.oa-section-collapsed > .oa-section-heading ~ * {
  display: none !important;
}

.scroll-anchor {
  scroll-margin-top: 5rem;
}

/* Page cards --------------------------------------------------------------- */
.oa-page-card {
  border: 1px solid rgba(0, 161, 254, 0.08);
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease,
    transform 0.15s ease;
  cursor: pointer;
}

.oa-page-card:hover {
  border-color: var(--theme-default);
  box-shadow: 0 8px 24px rgba(0, 161, 254, 0.12);
  transform: translateY(-2px);
}

.oa-icon-wrap {
  display: inline-flex;
  width: 3rem;
  height: 3rem;
  min-width: 3rem;
  align-items: center;
  justify-content: center;
  border-radius: 0.9rem;
  background: rgba(0, 161, 254, 0.08);
}

.oa-icon-wrap--primary {
  background: rgba(0, 161, 254, 0.1);
}

.oa-icon-wrap--alert {
  background: rgba(220, 53, 69, 0.1);
}

.oa-icon-wrap--muted {
  background: rgba(108, 117, 125, 0.12);
}

.oa-bullets {
  padding-left: 1.1rem;
  margin: 0;
}

.oa-bullets li + li {
  margin-top: 0.2rem;
}

/* Flow roadmaps ------------------------------------------------------------ */
.oa-flow {
  --oa-flow-accent: var(--theme-default);
  position: relative;
  display: grid;
  grid-template-columns: 3.25rem minmax(0, 1fr);
  gap: 0 1.25rem;
  counter-reset: oa-flow-step;
  max-width: 76rem;
  margin: 0 auto;
}

.oa-flow::before {
  content: '';
  position: absolute;
  top: 1.25rem;
  bottom: 1.25rem;
  left: 1.57rem;
  width: 2px;
  background: linear-gradient(
    180deg,
    rgba(108, 117, 125, 0.35),
    rgba(0, 161, 254, 0.45) 35%,
    rgba(111, 66, 193, 0.4) 70%,
    rgba(25, 135, 84, 0.45)
  );
}

.oa-step {
  counter-increment: oa-flow-step;
  position: relative;
  grid-column: 2;
  margin-bottom: 1.15rem;
  background: #fff;
  border: 1px solid rgba(15, 23, 42, 0.09);
  border-radius: 0.75rem;
  padding: 1rem 1.1rem 1.05rem;
  box-shadow:
    0 1px 0 rgba(15, 23, 42, 0.02),
    0 6px 18px rgba(15, 23, 42, 0.05);
  transition:
    border-color 0.18s ease,
    box-shadow 0.18s ease,
    transform 0.18s ease;
}

.oa-step::before {
  content: counter(oa-flow-step);
  position: absolute;
  top: 0.8rem;
  left: -4.15rem;
  z-index: 2;
  display: flex;
  width: 2.8rem;
  height: 2.8rem;
  align-items: center;
  justify-content: center;
  border: 4px solid #fff;
  border-radius: 50%;
  background: var(--oa-flow-accent);
  color: #fff;
  box-shadow: 0 0 0 1px rgba(15, 23, 42, 0.08);
  font-size: 0.82rem;
  font-weight: 800;
}

.oa-step::after {
  content: '';
  position: absolute;
  top: 1.7rem;
  left: -0.45rem;
  width: 0.8rem;
  height: 0.8rem;
  transform: rotate(45deg);
  border-bottom: 1px solid rgba(15, 23, 42, 0.09);
  border-left: 1px solid rgba(15, 23, 42, 0.09);
  background: inherit;
}

.oa-step:hover {
  transform: translateX(3px);
  border-color: color-mix(in srgb, var(--oa-flow-accent) 45%, transparent);
  box-shadow:
    0 1px 0 rgba(15, 23, 42, 0.02),
    0 10px 24px rgba(15, 23, 42, 0.08);
}

.oa-step--source {
  --oa-flow-accent: #6c757d;
  background: linear-gradient(90deg, rgba(108, 117, 125, 0.055), #fff 16%);
}

.oa-step--branch {
  --oa-flow-accent: #d39e00;
  background: linear-gradient(90deg, rgba(255, 193, 7, 0.075), #fff 16%);
}

.oa-step--trigger {
  --oa-flow-accent: #0d6efd;
  background: linear-gradient(90deg, rgba(13, 110, 253, 0.06), #fff 16%);
}

.oa-step--store {
  --oa-flow-accent: #6f42c1;
  background: linear-gradient(90deg, rgba(111, 66, 193, 0.06), #fff 16%);
}

.oa-step--end {
  --oa-flow-accent: #198754;
  background: linear-gradient(90deg, rgba(25, 135, 84, 0.065), #fff 16%);
}

.oa-step__title {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  margin-bottom: 0.35rem;
  color: #172033;
  font-size: 0.98rem;
  font-weight: 700;
  line-height: 1.35;
}

.oa-step__title::before {
  content: 'Step ' counter(oa-flow-step);
  flex: 0 0 auto;
  border: 1px solid color-mix(in srgb, var(--oa-flow-accent) 25%, transparent);
  border-radius: 999px;
  background: color-mix(in srgb, var(--oa-flow-accent) 8%, #fff);
  padding: 0.12rem 0.45rem;
  color: var(--oa-flow-accent);
  font-size: 0.61rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.oa-step__desc {
  max-width: 58rem;
  color: #667085;
  font-size: 0.9rem;
  line-height: 1.62;
}

.oa-arrow {
  display: none;
}

@media (min-width: 1200px) {
  .oa-flow {
    display: flex;
    flex-direction: column;
    max-width: none;
  }

  .oa-flow::before {
    left: 50%;
    transform: translateX(-1px);
  }

  .oa-step {
    width: calc(50% - 2.5rem);
    min-height: 7.5rem;
    margin-bottom: 1.25rem;
  }

  .oa-step:nth-of-type(4n + 1) {
    align-self: flex-start;
  }

  .oa-step:nth-of-type(4n + 3) {
    align-self: flex-end;
  }

  .oa-step:nth-of-type(4n + 1)::before {
    right: -4.05rem;
    left: auto;
  }

  .oa-step:nth-of-type(4n + 3)::before {
    left: -4.05rem;
  }

  .oa-step:nth-of-type(4n + 1)::after {
    right: -0.45rem;
    left: auto;
    border: 0;
    border-top: 1px solid rgba(15, 23, 42, 0.09);
    border-right: 1px solid rgba(15, 23, 42, 0.09);
  }

  .oa-step:nth-of-type(4n + 1):hover {
    transform: translateX(-3px);
  }
}

@media (max-width: 575.98px) {
  .oa-flow {
    grid-template-columns: 2.5rem minmax(0, 1fr);
    gap: 0 0.65rem;
  }

  .oa-flow::before {
    left: 1.2rem;
  }

  .oa-step {
    padding: 0.9rem;
  }

  .oa-step::before {
    left: -3.15rem;
    width: 2.35rem;
    height: 2.35rem;
    border-width: 3px;
    font-size: 0.72rem;
  }

  .oa-step__title {
    display: block;
  }

  .oa-step__title::before {
    display: table;
    margin-bottom: 0.35rem;
  }
}

/* Tables ------------------------------------------------------------------- */
.oa-glossary-table thead,
.oa-tabs-table thead,
.oa-states-table thead,
.oa-roles-table thead {
  background: rgba(0, 161, 254, 0.06);
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.oa-glossary-table code,
.oa-states-table code {
  background: rgba(0, 0, 0, 0.04);
  border-radius: 0.25rem;
  padding: 0.05rem 0.3rem;
}

/* Use-case decision list ----------------------------------------------------- */
.oa-usecase-group + .oa-usecase-group {
  margin-top: 2rem;
}

.oa-usecase-group__head {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 0.4rem;
}

.oa-usecase-group__head::after {
  content: '';
  flex: 1;
  height: 1px;
  background: linear-gradient(90deg, rgba(0, 161, 254, 0.25), rgba(0, 161, 254, 0.02));
}

.oa-usecase-group__icon {
  display: inline-flex;
  width: 1.9rem;
  height: 1.9rem;
  align-items: center;
  justify-content: center;
  border-radius: 0.5rem;
  background: rgba(0, 161, 254, 0.08);
  color: var(--theme-default);
  font-size: 0.8rem;
}

.oa-usecase-group__label {
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: var(--theme-default);
  white-space: nowrap;
}

.oa-usecase-list {
  margin: 0;
  padding: 0;
  list-style: none;
}

.oa-usecase-row {
  display: grid;
  grid-template-columns: minmax(0, 1.05fr) minmax(0, 0.85fr) minmax(0, 1.55fr);
  gap: 0.5rem 1.75rem;
  align-items: start;
  padding: 1rem 0.9rem;
  border-left: 3px solid transparent;
  border-radius: 0 0.6rem 0.6rem 0;
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease;
}

.oa-usecase-row + .oa-usecase-row {
  border-top: 1px solid rgba(15, 23, 42, 0.06);
}

.oa-usecase-row:hover {
  background: rgba(0, 161, 254, 0.035);
  border-left-color: var(--theme-default);
}

.oa-usecase-row__situation {
  color: #172033;
  font-size: 0.95rem;
  font-weight: 600;
  line-height: 1.45;
}

.oa-usecase-row__target-hint {
  margin-top: 0.4rem;
  color: #667085;
  font-size: 0.78rem;
  line-height: 1.4;
}

.oa-usecase-row__guidance {
  color: #344054;
  font-size: 0.9rem;
  line-height: 1.55;
}

.oa-usecase-row__hint {
  display: flex;
  align-items: baseline;
  gap: 0.4rem;
  color: #667085;
  font-size: 0.8rem;
  line-height: 1.45;
}

.oa-usecase-row__hint .fa {
  color: #d39e00;
  font-size: 0.85rem;
}

.oa-usecase-link {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.3rem 0.7rem 0.3rem 0.35rem;
  border: 1px solid rgba(0, 161, 254, 0.25);
  border-radius: 999px;
  background: #fff;
  color: var(--theme-default);
  font-size: 0.82rem;
  font-weight: 600;
  text-decoration: none;
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease,
    box-shadow 0.15s ease;
}

.oa-usecase-link__icon {
  display: inline-flex;
  width: 1.55rem;
  height: 1.55rem;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(0, 161, 254, 0.1);
  font-size: 0.72rem;
}

.oa-usecase-link__label {
  min-width: 0;
}

.oa-usecase-link__chevron {
  flex: 0 0 auto;
  font-size: 0.58rem;
  opacity: 0.65;
  transition: transform 0.15s ease;
}

.oa-usecase-link:hover,
.oa-usecase-link:focus-visible {
  background: rgba(0, 161, 254, 0.07);
  border-color: var(--theme-default);
  color: var(--theme-default);
  box-shadow: 0 3px 10px rgba(0, 161, 254, 0.12);
}

.oa-usecase-link:hover .oa-usecase-link__chevron,
.oa-usecase-link:focus-visible .oa-usecase-link__chevron {
  transform: translateX(2px);
}

@media (max-width: 991.98px) {
  .oa-usecase-row {
    grid-template-columns: minmax(0, 1fr);
    gap: 0.6rem;
    padding: 0.9rem 0.65rem;
  }
}

.oa-role-note {
  background: rgba(111, 66, 193, 0.1);
  color: #6f42c1;
  border: 1px solid rgba(111, 66, 193, 0.18);
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

/* State badges ------------------------------------------------------------- */
.oa-state-badge {
  background: rgba(108, 117, 125, 0.12);
  color: var(--bs-body-color);
  border: 1px solid rgba(108, 117, 125, 0.18);
  font-weight: 500;
  font-family: var(--bs-font-monospace, monospace);
  font-size: 0.78rem;
}

.oa-state-badge--target {
  background: rgba(25, 135, 84, 0.12);
  color: #146c43;
  border-color: rgba(25, 135, 84, 0.25);
}

/* Roles --------------------------------------------------------------------- */
.oa-role-pill {
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.7rem;
  letter-spacing: 0.06em;
}

.oa-role-pill--yes {
  background: rgba(25, 135, 84, 0.12);
  color: #146c43;
}

.oa-role-pill--no {
  background: rgba(108, 117, 125, 0.12);
  color: var(--bs-secondary-color, #6c757d);
}

/* Accordion tweaks --------------------------------------------------------- */
.oa-faq {
  display: grid;
  gap: 0.75rem;
}

.oa-faq .accordion-item {
  overflow: hidden;
  border: 1px solid rgba(15, 23, 42, 0.09);
  border-radius: 0.7rem;
  background: #fff;
  box-shadow:
    0 1px 0 rgba(15, 23, 42, 0.02),
    0 5px 15px rgba(15, 23, 42, 0.04);
}

.oa-faq .accordion-button {
  gap: 0.85rem;
  padding: 1rem 1.15rem;
  color: #172033;
  font-size: 0.96rem;
  font-weight: 700;
  line-height: 1.45;
}

.oa-faq .accordion-button::after {
  width: 1rem;
  height: 1rem;
  background-size: 1rem;
}

.oa-faq .accordion-button:hover {
  background: rgba(0, 161, 254, 0.035);
}

.oa-faq-number {
  display: inline-flex;
  width: 2rem;
  min-width: 2rem;
  height: 2rem;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(0, 161, 254, 0.2);
  border-radius: 50%;
  background: rgba(0, 161, 254, 0.08);
  color: var(--theme-default);
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.04em;
}

.oa-faq-question {
  flex: 1 1 auto;
}

.oa-faq .accordion-button:not(.collapsed) .oa-faq-number {
  border-color: var(--theme-default);
  background: var(--theme-default);
  color: #fff;
}

.oa-faq .accordion-body {
  border-top: 1px solid rgba(15, 23, 42, 0.06);
  background: #fafbfd;
  padding: 1rem 1.15rem 1.15rem 4rem;
}

.oa-faq-answer {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  max-width: 70rem;
  color: #536078;
  font-size: 0.9rem;
  line-height: 1.7;
}

.oa-faq-answer-icon {
  display: inline-flex;
  width: 1.8rem;
  min-width: 1.8rem;
  height: 1.8rem;
  align-items: center;
  justify-content: center;
  border-radius: 0.45rem;
  background: rgba(76, 148, 133, 0.1);
  color: var(--theme-secondary);
}

.accordion-button:not(.collapsed) {
  background: rgba(0, 161, 254, 0.06);
  color: var(--bs-body-color);
  box-shadow: none;
}

.accordion-button:focus {
  box-shadow: none;
  border-color: rgba(0, 161, 254, 0.25);
}

@media (max-width: 575.98px) {
  .oa-faq .accordion-button {
    align-items: flex-start;
    padding: 0.9rem;
  }

  .oa-faq .accordion-body {
    padding: 0.9rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .oa-page-card {
    transition: border-color 0.1s ease;
  }
  .oa-page-card:hover {
    transform: none;
  }
}
</style>
